import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type NoticeCardTone = "info" | "success" | "warning" | "cream" | "neutral";

export interface NoticeCardProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Color tone. Default 'info'. */
  tone?: NoticeCardTone;
  /** Leading icon. Defaults to a tone-appropriate lucide icon. Pass `null` to omit. */
  icon?: React.ReactNode | null;
  /** Optional small uppercase label rendered above the title. */
  eyebrow?: React.ReactNode;
  /** Headline. Required for accessibility — it is rendered as a heading. */
  title: React.ReactNode;
  /** Heading level for the title. Default 'h3'. */
  headingLevel?: "h2" | "h3" | "h4";
  /** Supporting copy below the title. */
  description?: React.ReactNode;
  /** Trailing action (link or button). */
  action?: React.ReactNode;
}

const ICON_BY_TONE: Record<NoticeCardTone, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  cream: Info,
  neutral: AlertCircle,
};

/**
 * Full-surface informational card. Different from `<Alert>` (a left-bordered
 * annotation banner) and from `<SystemMessage>` (a centered, dataset-replacing
 * empty state). A NoticeCard is a primary content tile that *is* the message:
 * tinted background, eyebrow label, headline, body copy, and optional action.
 */
export const NoticeCard = React.forwardRef<HTMLElement, NoticeCardProps>(function NoticeCard(
  {
    action,
    children,
    className,
    description,
    eyebrow,
    headingLevel = "h3",
    icon,
    title,
    tone = "info",
    ...props
  },
  ref,
) {
  const ToneIcon = ICON_BY_TONE[tone];
  const renderedIcon =
    icon === null ? null : icon !== undefined ? (
      icon
    ) : (
      <ToneIcon aria-hidden className="cw-v2-notice-card__icon" />
    );
  const TitleTag = headingLevel as React.ElementType;

  return (
    <section
      ref={ref}
      className={cx("cw-v2-notice-card", `cw-v2-notice-card--${tone}`, className)}
      {...props}
    >
      {renderedIcon ? (
        <span aria-hidden className="cw-v2-notice-card__icon-wrap">
          {renderedIcon}
        </span>
      ) : null}
      <div className="cw-v2-notice-card__body">
        {eyebrow ? <div className="cw-v2-notice-card__eyebrow">{eyebrow}</div> : null}
        <TitleTag className="cw-v2-notice-card__title">{title}</TitleTag>
        {description ? <div className="cw-v2-notice-card__description">{description}</div> : null}
        {children ? <div className="cw-v2-notice-card__content">{children}</div> : null}
      </div>
      {action ? <div className="cw-v2-notice-card__action">{action}</div> : null}
    </section>
  );
});
NoticeCard.displayName = "NoticeCard";
